/**
 * The rules for a Notice's attachments: which files can be added, how big they
 * can be, and how many. Add Event and Planner rows both use these, so they
 * accept the same files.
 */
import {
  MAX_VIDEO_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_MB,
  isAllowedNoticeVideoUpload,
} from "../../constants/uploadLimits";
import type { NoticeUploadedFile } from "./noticePayload";

/** The most attachments one Notice can have. */
export const NOTICE_ATTACHMENTS_MAX = 20;

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/x-msvideo",
  // WebM removed - not supported on iOS
  "video/x-ms-wmv",
  "video/3gpp",
];

const VIDEO_EXTENSIONS = [".mp4", ".mov", ".mpeg", ".avi", ".wmv", ".3gp"];
const MB = 1024 * 1024;

/** iPhone sometimes reports the wrong type for videos, so the extension counts too. */
const isVideo = (file: File) =>
  file.type.startsWith("video/") ||
  VIDEO_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

/** Why this kind of file can't be attached, or undefined when it can. */
export function noticeFileTypeProblem(file: File): string | undefined {
  if (isVideo(file)) {
    if (isAllowedNoticeVideoUpload(file)) return undefined;
    const isWebm = file.type === "video/webm" || file.name.toLowerCase().endsWith(".webm");
    return isWebm
      ? "WebM is not supported on all devices. Please use MP4 or MOV."
      : `Video type ${file.type || "unknown"} not allowed. Please use MP4 or MOV.`;
  }
  return ALLOWED_TYPES.includes(file.type) ? undefined : `File type ${file.type || "unknown"} not allowed.`;
}

/**
 * Why the file is too big, or undefined. Image notices allow larger images (25MB),
 * videos have their own limit, anything else 10MB. `toUpload` is the file as it
 * will be uploaded (e.g. an optimised image).
 */
export function noticeFileSizeProblem(
  file: File,
  toUpload: { name: string; size: number },
  isImageNotice: boolean,
): string | undefined {
  const video = isVideo(file);
  const [maxMB, maxBytes] =
    file.type.startsWith("image/") && isImageNotice
      ? [25, 25 * MB]
      : video
        ? [MAX_VIDEO_UPLOAD_MB, MAX_VIDEO_UPLOAD_BYTES]
        : [10, 10 * MB];
  if (toUpload.size <= maxBytes) return undefined;
  const sizeMB = (toUpload.size / MB).toFixed(2);
  return `${video ? "Video" : "File"} "${toUpload.name}" is too large (${sizeMB}MB). Max ${maxMB}MB.`;
}

/** The attachments with the new ones added, up to the limit. */
export const withAddedAttachments = (
  existing: NoticeUploadedFile[],
  added: NoticeUploadedFile[],
): NoticeUploadedFile[] => [...existing, ...added].slice(0, Math.max(NOTICE_ATTACHMENTS_MAX, existing.length));
